/**
 * Push Notification Registration & Handling
 *
 * Registers for Expo push notifications, stores the token in Supabase,
 * and handles incoming notifications (foreground + background).
 */

import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import * as Notifications from 'expo-notifications';
import * as Device from 'expo-device';
import { useRouter } from 'expo-router';
import { supabase, isSupabaseConfigured } from '@/lib/supabase/client';
import { useAuth } from '@/lib/supabase/auth';
import { mode } from '@/lib/config/app-mode';
import { reportError } from '@/lib/errors';

// Configure how notifications appear when app is in foreground (native only)
if (Platform.OS !== 'web') {
  Notifications.setNotificationHandler({
    handleNotification: async () => ({
      shouldShowAlert: true,
      shouldPlaySound: true,
      shouldSetBadge: true,
      shouldShowBanner: true,
      shouldShowList: true,
    }),
  });
}

/**
 * Request push notification permissions and get Expo push token.
 * Returns null if permissions denied or not a physical device.
 */
async function registerForPushNotifications(): Promise<string | null> {
  // Push notifications only work on physical devices
  if (!Device.isDevice) {
    // Push registration skipped: Skipping — not a physical device');
    return null;
  }

  // Web doesn't support Expo push tokens
  if (Platform.OS === 'web') {
    return null;
  }

  // Check existing permissions
  const { status: existingStatus } = await Notifications.getPermissionsAsync();
  let finalStatus = existingStatus;

  // Request if not already granted
  if (existingStatus !== 'granted') {
    const { status } = await Notifications.requestPermissionsAsync();
    finalStatus = status;
  }

  if (finalStatus !== 'granted') {
    // Push registration skipped: Permission not granted');
    return null;
  }

  // Android requires a notification channel
  if (Platform.OS === 'android') {
    await Notifications.setNotificationChannelAsync('default', {
      name: 'Default',
      importance: Notifications.AndroidImportance.HIGH,
      vibrationPattern: [0, 250, 250, 250],
      lightColor: '#00D9D9',
    });
  }

  // Get the Expo push token
  try {
    const tokenData = await Notifications.getExpoPushTokenAsync({
      projectId: process.env.EXPO_PUBLIC_PROJECT_ID,
    });
    return tokenData.data;
  } catch (err) {
    reportError(err, { context: 'Push.getExpoPushToken', silent: true });
    return null;
  }
}

/**
 * Save push token to Supabase (upsert — handles re-registration).
 */
async function savePushToken(userId: string, token: string): Promise<void> {
  if (!isSupabaseConfigured() || mode.isMock) return;

  const { error } = await supabase
    .from('push_tokens')
    .upsert(
      {
        user_id: userId,
        token,
        platform: Platform.OS,
        device_name: Device.deviceName ?? (`${Device.brand ?? ''} ${Device.modelName ?? ''}`.trim() || null),
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id,token' }
    );

  if (error) {
    reportError(error, { context: 'Push.saveToken', silent: true });
  }
}

/**
 * Remove push token from Supabase (on sign-out).
 */
export async function removePushToken(userId: string, token: string): Promise<void> {
  if (!isSupabaseConfigured() || mode.isMock) return;

  await supabase
    .from('push_tokens')
    .delete()
    .eq('user_id', userId)
    .eq('token', token)
    .then(({ error }) => {
      if (error) reportError(error, { context: 'Push.removeToken', silent: true });
    });
}

/**
 * Update app badge count based on unread notifications.
 */
export async function updateBadgeCount(userId: string): Promise<void> {
  if (Platform.OS === 'web' || mode.isMock) return;

  try {
    if (!isSupabaseConfigured()) {
      await Notifications.setBadgeCountAsync(0);
      return;
    }

    const { count, error } = await supabase
      .from('notifications')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('read', false);

    if (!error) {
      await Notifications.setBadgeCountAsync(count ?? 0);
    }
  } catch {
    // Badge count is best-effort
  }
}

/**
 * Hook: Register for push notifications and handle incoming notifications.
 * Call this once in the root layout.
 */
export function usePushNotifications() {
  const { user } = useAuth();
  const router = useRouter();
  const tokenRef = useRef<string | null>(null);
  const notificationListener = useRef<Notifications.Subscription | null>(null);
  const responseListener = useRef<Notifications.Subscription | null>(null);

  // Register token when user is authenticated
  useEffect(() => {
    if (!user?.id || Platform.OS === 'web' || mode.isMock) return;

    (async () => {
      const token = await registerForPushNotifications();
      if (token) {
        tokenRef.current = token;
        await savePushToken(user.id, token);
        await updateBadgeCount(user.id);
      }
    })();
  }, [user?.id]);

  // Listen for incoming notifications (foreground + tap)
  useEffect(() => {
    if (!user?.id || Platform.OS === 'web') return;
    const userId = user.id; // freeze to avoid stale closure

    notificationListener.current = Notifications.addNotificationReceivedListener(() => {
      updateBadgeCount(userId);
    });

    responseListener.current = Notifications.addNotificationResponseReceivedListener(() => {
      router.push('/notifications');
      updateBadgeCount(userId);
    });

    return () => {
      notificationListener.current?.remove();
      responseListener.current?.remove();
    };
  }, [user?.id, router]);

  return { pushToken: tokenRef.current };
}
